import { useEffect } from 'react'

import InfiniteScroll from 'react-infinite-scroll-component'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import SubmissionCard from '@components/Submission/Card'
import { CardGridWithInfinteScroll } from '@components/UI/CardGrid/CardGrid.styled'
import Loading from '@components/UI/Loading'
import { ICampaignContextProps } from '@context/Campaign'
import { ISubmissionListFilterContextProps } from '@context/SubmissionListFilter'
import { withCampaign, withRememberScrollPosition, withSubmissionListFilter } from '@hocs'
import { useIsMounted } from '@hooks/useIsMounted'
import { IApplicationState } from '@store'
import {
  fetchSubmissions,
  ISubmission,
  selectFilteredSubmissions,
  selectFilteredSubmissionsNextLink,
  selectIsFetching,
  selectIsLoadingInitialList,
} from '@store/submissions'
import { isEmpty } from '@utils'
import SubmissionCardListEmpty from './Empty'

interface IInternalProps extends ISubmissionListFilterContextProps, ICampaignContextProps {
  isSocialSubmission?: boolean
  submissions: ReadonlyArray<ISubmission>
  hasMore: boolean
  hasEmptyList?: boolean
  isLoadingInitialList?: boolean
}

const SubmissionCardList: React.FC<IInternalProps> = ({
  filter,
  sortBy,
  declinedFilter,
  campaign,
}) => {
  const { id: campaignId } = campaign
  const isMounted = useIsMounted()
  const dispatch = useDispatch()
  const queryParams = { filter, sortBy, declinedFilter }

  const { hasEmptyList, hasMore, isLoadingInitialList, submissions } = useSelector(
    (state: IApplicationState) => {
      const usedFilter = declinedFilter || filter

      const isFetching = selectIsFetching(state)

      const submissions = selectFilteredSubmissions(state, campaignId, usedFilter)

      const hasMore = Boolean(selectFilteredSubmissionsNextLink(state, campaignId, usedFilter))

      return {
        submissions,
        isLoadingInitialList: selectIsLoadingInitialList(state, campaignId, usedFilter),
        hasEmptyList: !isFetching && isEmpty(submissions),
        hasMore,
      }
    },
    shallowEqual,
  )

  useEffect(() => {
    dispatch(fetchSubmissions(campaignId, queryParams))
  }, [])

  useEffect(() => {
    if (isMounted) {
      dispatch(fetchSubmissions(campaignId, { ...queryParams, resetList: true }))
    }
  }, [sortBy])

  useEffect(() => {
    if (isMounted) {
      dispatch(fetchSubmissions(campaignId, { ...queryParams, resetList: false }))
    }
  }, [filter, declinedFilter])

  const fetchSubmissionsUsingNext = () => dispatch(fetchSubmissions(campaignId, queryParams, true))

  if (isLoadingInitialList) {
    return <Loading height={30} />
  }

  if (hasEmptyList) {
    return <SubmissionCardListEmpty submissionFilter={filter} campaignId={campaignId} />
  }

  return (
    <CardGridWithInfinteScroll gap={0.9375}>
      <InfiniteScroll
        dataLength={submissions.length}
        next={fetchSubmissionsUsingNext}
        loader={<Loading height={30} />}
        hasMore={hasMore}
        scrollThreshold={0.8}
        style={{ overflow: 'hidden' }}
      >
        <div>
          {submissions.map((submission) => (
            <SubmissionCard key={submission.id} submissionId={submission.id} />
          ))}
        </div>
      </InfiniteScroll>
    </CardGridWithInfinteScroll>
  )
}

export default withCampaign(
  withSubmissionListFilter(withRememberScrollPosition(SubmissionCardList)),
)
